import { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { apiFetch, ApiError, friendlyMessage } from '../../api/apiClient';
import { LoadingState, ErrorState } from '../../shared/components/status';
import { GREEN, CHARCOAL, BORDER, MUTED, MUTED_FAINT, GREEN_HOVER, STONE50, ERROR,
  labelMono, bodyFont, pageTitle, pageTitleAccent, pageDescription, PAGE_PX } from '../../theme/tokens2';

const GREEN_FAINT = 'rgba(33,131,51,0.06)';
const GREEN_LIGHT = 'rgba(33,131,51,0.30)';
const BORDER_MID  = 'rgba(0,0,0,0.16)';

// ─── Auto-save timing ─────────────────────────────────────────────────────────
// Clicks save straight away. Typing saves 1.5 seconds after the customer
// stops typing (and when they click out of the box), never on every letter.
// A failed save retries by itself after 5s, then 15s, then every 30s.
const TYPING_DELAY = 1500;
const RETRY_DELAYS = [5000, 15000, 30000];

// ─── Tile config ──────────────────────────────────────────────────────────────

const APPLIANCE_TILES = [
  { key: 'Hob',           icon: '🔥' },
  { key: 'Hood',          icon: '💨' },
  { key: 'Oven',          icon: '⚙️'  },
  { key: 'Microwave',     icon: '📦' },
  { key: 'Water Filter',  icon: '💧' },
  { key: 'Dishwasher',    icon: '🫧' },
];

const PET_TILES = [
  { key: 'Dogs',          icon: '🐕' },
  { key: 'Cats',          icon: '🐈' },
  { key: 'Tanks / Fish',  icon: '🐠' },
];

const ACTIVITY_TILES = [
  { key: 'Fitness Room',  icon: '🏋️' },
  { key: 'Workshop',      icon: '🔧' },
  { key: 'Music Studio',  icon: '🎵' },
  { key: 'Other',         icon: '＋' },
];

const BULKY_TILES = [
  { key: 'Bicycles',      icon: '🚲' },
  { key: 'Prams',         icon: '🍼' },
  { key: 'Golf Bags',     icon: '⛳' },
  { key: 'Large Luggage', icon: '🧳' },
];

const FUTURE_TILES = [
  { key: 'Expecting a child',       icon: '👶' },
  { key: 'Ageing parent moving in', icon: '👴' },
  { key: 'Child moving out',        icon: '🚪' },
];

const TILE_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: '8px',
};

const FIELD_SX = { mb: '26px' };

// ─── Occupant helpers ─────────────────────────────────────────────────────────

/** Blank occupant record for a given type. */
const newOccupant = (type, pairId = null) => ({
  type, pairId,
  alias:         '',
  agerange:      '',
  wardrobespace: '',
  workstudy:     '',
});

/**
 * Works out a label from an occupant's type and its position among people of
 * the same type. Labels renumber themselves when someone is removed.
 * Couples are numbered by pair: Couple 1 — A, Couple 1 — B, Couple 2 — A…
 */
function getLabel(occupant, allOccupants) {
  const sameType = allOccupants.filter((o) => o.type === occupant.type);
  const idx = sameType.indexOf(occupant);
  if (occupant.type === 'couple') {
    const pairNum = Math.floor(idx / 2) + 1;
    const partner = idx % 2 === 0 ? 'A' : 'B';
    return `Couple ${pairNum} — ${partner}`;
  }
  if (occupant.type === 'single') return `Single ${idx + 1}`;
  return `Helper ${idx + 1}`;
}

// ─── Small reusable pieces ────────────────────────────────────────────────────

function FieldLabel({ children, tag, id }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px', mb: '8px' }}>
      <Typography id={id} sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>
        {children}
      </Typography>
      {tag && (
        <Typography sx={{
          ...labelMono, fontSize: '8px',
          border: `1px solid ${BORDER_MID}`, px: '6px', py: '1px',
          color: MUTED_FAINT, flexShrink: 0,
        }}>
          {tag}
        </Typography>
      )}
    </Box>
  );
}

function Hint({ children }) {
  return (
    <Typography sx={{
      fontSize: '11px', color: MUTED_FAINT, mt: '5px',
      lineHeight: 1.55, fontStyle: 'italic', ...bodyFont,
    }}>
      {children}
    </Typography>
  );
}

function ClusterHeader({ label }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '14px',
      mb: '28px', pb: '14px',
    }}>
      <Typography component="h2" sx={{ ...labelMono, fontSize: '11px', color: GREEN }}>{label}</Typography>
      <Box sx={{ flex: 1, height: '1px', background: BORDER }} />
    </Box>
  );
}

// Buttons where only one can be chosen (e.g. Yes / No)
function ToggleGroup({ options, value, onChange, labelledBy }) {
  return (
    <Box role="radiogroup" aria-labelledby={labelledBy} sx={{ display: 'flex', flexWrap: 'wrap' }}>
      {options.map((opt, i) => {
        const active = value === opt;
        return (
          <Box
            key={opt}
            component="button"
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt)}
            sx={{
              padding: '9px 18px',
              border: `1px solid ${active ? GREEN : BORDER_MID}`,
              background: active ? GREEN : '#fff',
              ...labelMono, fontSize: '9px', color: active ? '#fff' : MUTED,
              cursor: 'pointer',
              ml: i === 0 ? 0 : '-1px',
              position: active ? 'relative' : 'static',
              zIndex: active ? 1 : 0,
              transition: 'all .15s',
              '&:hover': { background: active ? GREEN_HOVER : STONE50, color: active ? '#fff' : CHARCOAL },
              '&:focus-visible': { outline: `2px solid ${CHARCOAL}`, outlineOffset: '1px', position: 'relative', zIndex: 2 },
            }}
          >
            {opt}
          </Box>
        );
      })}
    </Box>
  );
}

// A tile that can be ticked on and off (several can be chosen)
function Tile({ label, icon, selected, onToggle }) {
  return (
    <Box
      component="button"
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={onToggle}
      sx={{
        border: `1px solid ${selected ? GREEN : BORDER_MID}`,
        background: selected ? GREEN_FAINT : '#fff',
        padding: '11px 13px',
        cursor: 'pointer',
        position: 'relative',
        textAlign: 'left',
        transition: 'all .15s',
        '&:hover': {
          borderColor: selected ? GREEN : GREEN_LIGHT,
          background: selected ? GREEN_FAINT : 'rgba(33,131,51,0.03)',
        },
        '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '1px' },
      }}
    >
      <Box aria-hidden sx={{
        position: 'absolute', top: '7px', right: '8px',
        width: '13px', height: '13px',
        border: `1px solid ${selected ? GREEN : BORDER}`,
        background: selected ? GREEN : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '8px', color: selected ? '#fff' : 'transparent',
        transition: 'all .15s',
      }}>
        ✓
      </Box>
      <Typography component="span" aria-hidden sx={{ fontSize: '18px', mb: '5px', lineHeight: 1, display: 'block' }}>
        {icon}
      </Typography>
      <Typography component="span" sx={{
        ...labelMono, fontSize: '8px', color: selected ? GREEN : MUTED, lineHeight: 1.4, display: 'block',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── OccupantCard ─────────────────────────────────────────────────────────────

function OccupantCard({ label, data, onChange, onType, onTypeDone, onDelete, idBase }) {
  const inputSx = {
    width: '100%', boxSizing: 'border-box',
    border: `1px solid ${BORDER}`,
    background: STONE50,
    padding: '7px 10px',
    fontSize: '12px',
    color: CHARCOAL,
    ...bodyFont,
    outline: 'none',
    transition: 'border-color .2s, background .2s',
    '&:focus': { borderColor: GREEN, background: '#fff' },
  };
  const miniLabel = {
    ...labelMono, fontSize: '8px', color: MUTED, display: 'block', mb: '4px',
  };

  return (
    <Box sx={{ border: `1px solid ${BORDER_MID}`, padding: '16px', background: '#fff' }}>

      {/* Header: label + remove button */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: '14px', pb: '12px', borderBottom: `1px solid ${BORDER}`,
      }}>
        <Typography component="h3" sx={{
          ...labelMono, fontSize: '8px', color: GREEN,
          border: `1px solid ${GREEN_LIGHT}`, px: '7px', py: '2px',
          background: GREEN_FAINT,
        }}>
          {label.toUpperCase()}
        </Typography>

        <Box
          component="button"
          type="button"
          onClick={onDelete}
          aria-label={`Remove ${label}`}
          sx={{
            width: 24, height: 24,
            border: `1px solid ${BORDER_MID}`,
            background: 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: MUTED,
            fontSize: '12px', lineHeight: 1,
            transition: 'border-color .15s, color .15s, background .15s',
            '&:hover': { borderColor: ERROR, color: ERROR, background: 'rgba(180,35,24,0.06)' },
          }}
        >
          ×
        </Box>
      </Box>

      {/* Alias */}
      <Box sx={{ mb: '10px' }}>
        <Typography component="label" htmlFor={`${idBase}-alias`} sx={miniLabel}>Alias</Typography>
        <Box
          component="input"
          id={`${idBase}-alias`}
          value={data.alias}
          onChange={(e) => onType('alias', e.target.value)}
          onBlur={onTypeDone}
          placeholder="e.g. Mum, Dad, Alex"
          maxLength={24}
          sx={inputSx}
        />
      </Box>

      {/* Age + Wardrobe */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', mb: '10px' }}>
        <Box>
          <Typography component="label" htmlFor={`${idBase}-age`} sx={miniLabel}>Age range</Typography>
          <Box
            component="select"
            id={`${idBase}-age`}
            value={data.agerange}
            onChange={(e) => onChange('agerange', e.target.value)}
            sx={{ ...inputSx, appearance: 'none', cursor: 'pointer' }}
          >
            <option value="">Select…</option>
            {['0–6', '7–12', '13–17', '18–54', '55–64', '65+'].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </Box>
        </Box>
        <Box>
          <Typography component="label" htmlFor={`${idBase}-wardrobe`} sx={miniLabel}>Wardrobe size</Typography>
          <Box
            component="select"
            id={`${idBase}-wardrobe`}
            value={data.wardrobespace}
            onChange={(e) => onChange('wardrobespace', e.target.value)}
            sx={{ ...inputSx, appearance: 'none', cursor: 'pointer' }}
          >
            <option value="">Select…</option>
            {['Small', 'Medium', 'Large', 'Walk-in'].map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Work / study from home */}
      <Box>
        <Typography component="label" htmlFor={`${idBase}-work`} sx={miniLabel}>Work / Study from home</Typography>
        <Box
          component="select"
          id={`${idBase}-work`}
          value={data.workstudy}
          onChange={(e) => onChange('workstudy', e.target.value)}
          sx={{ ...inputSx, appearance: 'none', cursor: 'pointer' }}
        >
          <option value="">Select…</option>
          <option value="none">None</option>
          <option value="just a table">Just a table</option>
          <option value="dedicated desk">Dedicated desk + monitor</option>
          <option value="private space">Private space needed</option>
        </Box>
      </Box>
    </Box>
  );
}

function OpenQuestion({ id, label, value, onChange, onBlur, placeholder }) {
  return (
    <Box sx={{ mb: '26px' }}>
      <Box component="label" htmlFor={id} sx={{ display: 'block' }}>
        <FieldLabel tag="OPTIONAL">{label}</FieldLabel>
      </Box>
      <Box
        component="textarea"
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        rows={5}
        placeholder={placeholder}
        sx={{
          width: '100%', boxSizing: 'border-box',
          border: `1px solid ${BORDER_MID}`,
          background: '#fff',
          padding: '11px 14px',
          fontSize: '13px',
          color: CHARCOAL,
          ...bodyFont,
          outline: 'none',
          resize: 'vertical',
          lineHeight: 1.65,
          transition: 'border-color .2s',
          '&:focus': { borderColor: GREEN },
          '&::placeholder': { color: MUTED_FAINT },
        }}
      />
    </Box>
  );
}

// ─── SaveStatus ───────────────────────────────────────────────────────────────
// The small line at the top right that says whether answers are saved.
function SaveStatus({ status, onRetry }) {
  return (
    <Box
      aria-live="polite"
      sx={{
        position: 'sticky', top: 12, zIndex: 5,
        display: 'flex', justifyContent: 'flex-end',
        height: 0, // takes no space, so the form doesn't move
        pointerEvents: 'none',
      }}
    >
      {status !== 'idle' && (
        <Box sx={{
          pointerEvents: 'auto',
          background: '#fff', border: `1px solid ${BORDER}`, borderRadius: '999px',
          px: '12px', py: '4px', height: 'fit-content',
          ...bodyFont, fontSize: '11px',
          color: status === 'error' ? ERROR : status === 'saved' ? GREEN : MUTED,
        }}>
          {status === 'saving' && 'Saving…'}
          {status === 'saved' && '✓ All changes saved'}
          {status === 'error' && (
            <>
              Couldn't save ·{' '}
              <Box
                component="button"
                type="button"
                onClick={onRetry}
                sx={{ background: 'none', border: 'none', p: 0, color: 'inherit', font: 'inherit', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Retry
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Questionnaire() {

  // ── Loading ───────────────────────────────────────────────────────────────
  const [loadStatus, setLoadStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [loadError,  setLoadError]  = useState('');

  // ── Occupants ─────────────────────────────────────────────────────────────
  // Each entry: { type: 'couple'|'single'|'helper', pairId, alias, agerange, wardrobespace, workstudy }
  // Labels are worked out when shown (getLabel), not stored here.
  const [occupants, setOccupants] = useState([]);

  // ── Answers ───────────────────────────────────────────────────────────────
  const [pets, setPets] = useState({ Dogs: false, Cats: false, 'Tanks / Fish': false });
  const [heavyCooking, setHeavyCooking] = useState('');
  const [appliances, setAppliances] = useState({
    Hob: false, Hood: false, Oven: false,
    Microwave: false, 'Water Filter': false, Dishwasher: false,
  });
  const [altarSpace, setAltarSpace] = useState('');
  const [activityRooms, setActivityRooms] = useState({
    'Fitness Room': false, 'Workshop': false, 'Music Studio': false, 'Other': false,
  });
  const [bulkyItems, setBulkyItems] = useState({
    Bicycles: false, Prams: false, 'Golf Bags': false, 'Large Luggage': false,
  });
  const [hosting,     setHosting]     = useState('');
  const [noise,       setNoise]       = useState('');
  const [morningSync, setMorningSync] = useState('');
  const [maintenance, setMaintenance] = useState('');
  const [futureChanges, setFutureChanges] = useState({
    'Expecting a child': false,
    'Ageing parent moving in': false,
    'Child moving out': false,
  });
  const [budget, setBudget] = useState('');
  const [dreamFeeling,     setDreamFeeling]     = useState('');
  const [dreamInspiration, setDreamInspiration] = useState('');
  const [dreamFix,         setDreamFix]         = useState('');
  const [dreamDislikes,    setDreamDislikes]    = useState('');
  const [notes,            setNotes]            = useState('');

  // ── What gets sent when a save happens ────────────────────────────────────
  // Rebuilt on every render, so a save always sends the newest answers.
  // Both endpoints expect the FULL set of answers for their part.
  const latest = useRef({});
  latest.current = {
    profile: {
      heavycooking:              heavyCooking,
      kitchen_hob:               appliances.Hob,
      kitchen_hood:              appliances.Hood,
      kitchen_oven:              appliances.Oven,
      kitchen_microwave:         appliances.Microwave,
      kitchen_waterfilter:       appliances['Water Filter'],
      kitchen_dishwasher:        appliances.Dishwasher,
      pets_dogs:                 pets.Dogs,
      pets_cats:                 pets.Cats,
      pets_intank:               pets['Tanks / Fish'],
      altarspace:                altarSpace,
      activityrm_fitness:        activityRooms['Fitness Room'],
      activityrm_workshop:       activityRooms['Workshop'],
      activityrm_studio:         activityRooms['Music Studio'],
      activityrm_others:         activityRooms['Other'],
      bulky_bicycles:            bulkyItems.Bicycles,
      bulky_prams:               bulkyItems.Prams,
      bulky_golfbags:            bulkyItems['Golf Bags'],
      bulky_luggage:             bulkyItems['Large Luggage'],
      lifestyle_hosting:         hosting,
      lifestyle_noise:           noise,
      lifestyle_morningsync:     morningSync,
      lifestyle_maintenance:     maintenance,
      future_child:              futureChanges['Expecting a child'],
      future_agingparent:        futureChanges['Ageing parent moving in'],
      future_childleaving:       futureChanges['Child moving out'],
      budget,
      dream_feeling:             dreamFeeling,
      dream_inspiration:         dreamInspiration,
      dream_fix:                 dreamFix,
      dream_dislikes:            dreamDislikes,
      notes,
    },
    // Each person is sent with their label, so the order comes back the same.
    occupants: occupants.map((occ) => ({
      label:         getLabel(occ, occupants),
      alias:         occ.alias,
      agerange:      occ.agerange,
      wardrobespace: occ.wardrobespace,
      workstudy:     occ.workstudy,
    })),
  };

  // ── Auto-save ─────────────────────────────────────────────────────────────
  // Two separate parts, saved separately: 'profile' (all the answers) and
  // 'occupants' (the list of people). For each part:
  //   timer      — a save waiting to happen (e.g. the 1.5s typing pause)
  //   inFlight   — a save is being sent right now
  //   again      — something changed while sending: send once more afterwards
  //   status     — 'idle' | 'saving' | 'saved' | 'error'
  //   failures   — how many saves in a row have failed (for the retry wait)
  //   retryTimer — the automatic retry, after a failure
  // Only one save per part is ever sent at a time, so an older save can
  // never arrive after (and overwrite) a newer one.
  const parts = useRef({
    profile:   { timer: null, inFlight: false, again: false, status: 'idle', failures: 0, retryTimer: null },
    occupants: { timer: null, inFlight: false, again: false, status: 'idle', failures: 0, retryTimer: null },
  });
  const [saveStatus, setSaveStatus] = useState('idle');
  const statusRef = useRef('idle');
  const mounted   = useRef(true);
  const sendRef   = useRef(null);

  // One overall status for the little line at the top.
  const refreshStatus = () => {
    const all = Object.values(parts.current);
    let s = 'idle';
    if (all.some((p) => p.status === 'error'))       s = 'error';
    else if (all.some((p) => p.status === 'saving')) s = 'saving';
    else if (all.some((p) => p.status === 'saved'))  s = 'saved';
    statusRef.current = s;
    if (mounted.current) setSaveStatus(s);
  };

  sendRef.current = async (part, { keepalive = false } = {}) => {
    const p = parts.current[part];
    clearTimeout(p.timer);      p.timer = null;
    clearTimeout(p.retryTimer); p.retryTimer = null;
    if (p.inFlight) { p.again = true; return; }

    p.inFlight = true;
    p.status = 'saving';
    refreshStatus();
    try {
      if (part === 'profile') {
        await apiFetch('/api/householdprofile', { method: 'PATCH', body: latest.current.profile, keepalive });
      } else {
        await apiFetch('/api/occupants', { method: 'POST', body: latest.current.occupants, keepalive });
      }
      p.failures = 0;
      p.status = 'saved';
    } catch (err) {
      p.status = 'error';
      // An expired sign-in sends the customer to sign in (see apiClient.js),
      // so there's no point retrying. Otherwise, try again after a pause.
      const expired = err instanceof ApiError && err.status === 401;
      if (mounted.current && !expired) {
        const wait = RETRY_DELAYS[Math.min(p.failures, RETRY_DELAYS.length - 1)];
        p.failures += 1;
        p.retryTimer = setTimeout(() => sendRef.current(part), wait);
      }
    } finally {
      p.inFlight = false;
      refreshStatus();
      if (p.again) { p.again = false; sendRef.current(part); }
    }
  };

  // Ask for a save of one part, after `delay` ms. A newer request replaces
  // an older one that hasn't been sent yet.
  const schedule = useCallback((part, delay = 0) => {
    const p = parts.current[part];
    clearTimeout(p.timer);
    p.timer = setTimeout(() => sendRef.current(part), delay);
  }, []);

  // Send a waiting save now (used when the customer clicks out of a text box).
  const flushIfWaiting = useCallback((part) => {
    if (parts.current[part].timer) schedule(part, 0);
  }, [schedule]);

  // "Retry" button: try every failed part again right now.
  const retryNow = () => {
    Object.entries(parts.current).forEach(([part, p]) => {
      if (p.status === 'error') sendRef.current(part);
    });
  };

  // Leaving the Questionnaire (switching module): send anything still waiting.
  // keepalive lets the request finish even though this page is gone.
  useEffect(() => {
    mounted.current = true;
    const allParts = parts.current;
    return () => {
      mounted.current = false;
      Object.entries(allParts).forEach(([part, p]) => {
        clearTimeout(p.retryTimer);
        if (p.timer || p.status === 'error') sendRef.current(part, { keepalive: true });
      });
    };
  }, []);

  // Closing or refreshing the tab: send anything waiting. Only if the last
  // save failed, ask the browser to warn the customer before leaving.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      Object.entries(parts.current).forEach(([part, p]) => {
        if (p.timer) sendRef.current(part, { keepalive: true });
      });
      if (statusRef.current === 'error') { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // ── Change handlers: each one updates the screen, then asks for a save ───
  // (Loading the saved answers calls the setters directly, NOT these
  // handlers, so opening the page never sends a save.)
  const pick = (setter) => (value) => { setter(value); schedule('profile', 0); };
  const toggle = (setter, key) => { setter((prev) => ({ ...prev, [key]: !prev[key] })); schedule('profile', 0); };
  const type = (setter) => (value) => { setter(value); schedule('profile', TYPING_DELAY); };
  const typeDone = () => flushIfWaiting('profile');

  const addOccupant = (occType) => {
    if (occType === 'couple') {
      const pairId = Date.now();
      setOccupants((prev) => [...prev, newOccupant('couple', pairId), newOccupant('couple', pairId)]);
    } else {
      setOccupants((prev) => [...prev, newOccupant(occType)]);
    }
    schedule('occupants', 0);
  };

  const removeOccupant = (idx) => {
    const target = occupants[idx];
    if (target.type === 'couple') {
      setOccupants((prev) => prev.filter((o) => o.pairId !== target.pairId));
    } else {
      setOccupants((prev) => prev.filter((_, i) => i !== idx));
    }
    schedule('occupants', 0);
  };

  const updateOccupant = (idx, field, value, delay) => {
    setOccupants((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    schedule('occupants', delay);
  };

  // ── Load the saved answers ────────────────────────────────────────────────
  // If loading fails, the form is NOT shown. Showing an empty form and then
  // auto-saving it would wipe out the customer's real answers.
  const fetchExisting = useCallback(async () => {
    setLoadStatus('loading');
    try {
      const [p, rows] = await Promise.all([
        apiFetch('/api/householdprofile/my'),
        apiFetch('/api/occupants/my'),
      ]);

      if (p) {
        setHeavyCooking(p.heavycooking          ?? '');
        setAltarSpace(p.altarspace              ?? '');
        setHosting(p.lifestyle_hosting          ?? '');
        setNoise(p.lifestyle_noise              ?? '');
        setMorningSync(p.lifestyle_morningsync  ?? '');
        setMaintenance(p.lifestyle_maintenance  ?? '');
        setBudget(p.budget                      ?? '');
        setDreamFeeling(p.dream_feeling         ?? '');
        setDreamInspiration(p.dream_inspiration ?? '');
        setDreamFix(p.dream_fix                 ?? '');
        setDreamDislikes(p.dream_dislikes       ?? '');
        setNotes(p.notes                        ?? '');
        setAppliances({
          Hob:            p.kitchen_hob         ?? false,
          Hood:           p.kitchen_hood        ?? false,
          Oven:           p.kitchen_oven        ?? false,
          Microwave:      p.kitchen_microwave   ?? false,
          'Water Filter': p.kitchen_waterfilter ?? false,
          Dishwasher:     p.kitchen_dishwasher  ?? false,
        });
        setPets({
          Dogs:           p.pets_dogs   ?? false,
          Cats:           p.pets_cats   ?? false,
          'Tanks / Fish': p.pets_intank ?? false,
        });
        setActivityRooms({
          'Fitness Room': p.activityrm_fitness  ?? false,
          'Workshop':     p.activityrm_workshop ?? false,
          'Music Studio': p.activityrm_studio   ?? false,
          'Other':        p.activityrm_others   ?? false,
        });
        setBulkyItems({
          Bicycles:        p.bulky_bicycles ?? false,
          Prams:           p.bulky_prams    ?? false,
          'Golf Bags':     p.bulky_golfbags ?? false,
          'Large Luggage': p.bulky_luggage  ?? false,
        });
        setFutureChanges({
          'Expecting a child':       p.future_child        ?? false,
          'Ageing parent moving in': p.future_agingparent  ?? false,
          'Child moving out':        p.future_childleaving ?? false,
        });
      }

      // Rebuild the people list: the type comes from the saved label
      // ("Couple 1 — A" → couple, pair 1), in a stable order.
      const typeOrder = { couple: 0, single: 1, helper: 2 };
      const sorted = [...(rows || [])].sort((a, b) => {
        const ta = typeOrder[(a.label ?? '').toLowerCase().split(' ')[0]] ?? 1;
        const tb = typeOrder[(b.label ?? '').toLowerCase().split(' ')[0]] ?? 1;
        if (ta !== tb) return ta - tb;
        return (a.label ?? '').localeCompare(b.label ?? '');
      });
      setOccupants(sorted.map((row) => {
        const occType = (row.label ?? '').toLowerCase().split(' ')[0] || 'single';
        const pairId = occType === 'couple' ? parseInt((row.label ?? '').split(' ')[1], 10) : null;
        return {
          type: occType, pairId,
          alias:         row.alias         ?? '',
          agerange:      row.agerange      ?? '',
          wardrobespace: row.wardrobespace ?? '',
          workstudy:     row.workstudy     ?? '',
        };
      }));

      setLoadStatus('ready');
    } catch (err) {
      setLoadError(friendlyMessage(err));
      setLoadStatus('error');
    }
  }, []);

  useEffect(() => { fetchExisting(); }, [fetchExisting]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (loadStatus === 'loading') {
    return <LoadingState label="Loading your answers…" size="page" />;
  }

  if (loadStatus === 'error') {
    return (
      <ErrorState
        size="page"
        title="We couldn't load your questionnaire"
        message={`${loadError} Your saved answers are safe.`}
        onRetry={fetchExisting}
      />
    );
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', ...bodyFont }}>
      <Box sx={{ px: PAGE_PX, pt: 5, pb: 6 }}>

        <SaveStatus status={saveStatus} onRetry={retryNow} />

        {/* Page intro */}
        <Box sx={{ mb: 5 }}>
          <Typography component="h1" sx={pageTitle}>
            Tell us about your{' '}
            <Box component="span" sx={pageTitleAccent}>
              home and lifestyle.
            </Box>
          </Typography>
          <Typography sx={pageDescription}>
            Fill in as much or as little as you'd like — every answer helps your
            designer tailor their recommendations. Come back anytime to refine your brief.
          </Typography>
        </Box>

        {/* ── 01 HOUSEHOLD ── */}
        <Box component="section" sx={{ mb: 6 }}>
          <ClusterHeader label="HOUSEHOLD COMPOSITION" />

          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mb: '20px' }}>
            {[
              { label: '+ Add Couple',         occType: 'couple' },
              { label: '+ Add Single / Child', occType: 'single' },
              { label: '+ Add Helper',         occType: 'helper' },
            ].map(({ label, occType }) => (
              <Box
                key={occType}
                component="button"
                type="button"
                onClick={() => addOccupant(occType)}
                sx={{
                  ...labelMono, fontSize: '9px', color: MUTED,
                  border: `1px solid ${BORDER_MID}`,
                  background: '#fff',
                  px: '14px', py: '8px',
                  cursor: 'pointer',
                  transition: 'border-color .15s, color .15s, background .15s',
                  '&:hover': { borderColor: GREEN, color: GREEN, background: GREEN_FAINT },
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          {occupants.length > 0 ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: '10px',
              mb: '26px',
            }}>
              {occupants.map((occ, idx) => (
                <OccupantCard
                  key={idx}
                  idBase={`occupant-${idx}`}
                  label={getLabel(occ, occupants)}
                  data={occ}
                  onChange={(field, val) => updateOccupant(idx, field, val, 0)}
                  onType={(field, val) => updateOccupant(idx, field, val, TYPING_DELAY)}
                  onTypeDone={() => flushIfWaiting('occupants')}
                  onDelete={() => removeOccupant(idx)}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{
              border: `1px dashed ${BORDER_MID}`,
              px: 3, py: 4, mb: '26px',
              textAlign: 'center',
            }}>
              <Typography sx={{ fontSize: '13px', color: MUTED_FAINT, fontStyle: 'italic', ...bodyFont }}>
                No occupants added yet — use the buttons above to build your household.
              </Typography>
            </Box>
          )}

          <Box sx={FIELD_SX}>
            <FieldLabel tag="OPTIONAL" id="q-pets">Pets</FieldLabel>
            <Box role="group" aria-labelledby="q-pets" sx={TILE_GRID_SX}>
              {PET_TILES.map(({ key, icon }) => (
                <Tile key={key} label={key} icon={icon}
                  selected={pets[key]} onToggle={() => toggle(setPets, key)} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* ── 02 YOUR SPACE ── */}
        <Box component="section" sx={{ mb: 6 }}>
          <ClusterHeader label="YOUR SPACE" />

          <Box sx={FIELD_SX}>
            <FieldLabel id="q-cooking">Heavy cooking regularly?</FieldLabel>
            <ToggleGroup labelledBy="q-cooking" options={['Yes', 'No']} value={heavyCooking} onChange={pick(setHeavyCooking)} />
          </Box>

          <Box sx={FIELD_SX}>
            <FieldLabel tag="SELECT ALL THAT APPLY" id="q-appliances">Kitchen appliances needed</FieldLabel>
            <Box role="group" aria-labelledby="q-appliances" sx={TILE_GRID_SX}>
              {APPLIANCE_TILES.map(({ key, icon }) => (
                <Tile key={key} label={key} icon={icon}
                  selected={appliances[key]} onToggle={() => toggle(setAppliances, key)} />
              ))}
            </Box>
          </Box>

          <Box sx={FIELD_SX}>
            <FieldLabel id="q-altar">Space for an altar?</FieldLabel>
            <ToggleGroup labelledBy="q-altar" options={['Yes', 'No']} value={altarSpace} onChange={pick(setAltarSpace)} />
          </Box>

          <Box sx={FIELD_SX}>
            <FieldLabel tag="SELECT ALL THAT APPLY" id="q-activity">Dedicated activity rooms</FieldLabel>
            <Box role="group" aria-labelledby="q-activity" sx={TILE_GRID_SX}>
              {ACTIVITY_TILES.map(({ key, icon }) => (
                <Tile key={key} label={key} icon={icon}
                  selected={activityRooms[key]} onToggle={() => toggle(setActivityRooms, key)} />
              ))}
            </Box>
          </Box>

          <Box sx={FIELD_SX}>
            <FieldLabel tag="SELECT ALL THAT APPLY" id="q-bulky">Bulky items to accommodate</FieldLabel>
            <Box role="group" aria-labelledby="q-bulky" sx={TILE_GRID_SX}>
              {BULKY_TILES.map(({ key, icon }) => (
                <Tile key={key} label={key} icon={icon}
                  selected={bulkyItems[key]} onToggle={() => toggle(setBulkyItems, key)} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* ── 03 LIFESTYLE ── */}
        <Box component="section" sx={{ mb: 6 }}>
          <ClusterHeader label="LIFESTYLE" />

          <Box sx={FIELD_SX}>
            <FieldLabel tag="OPTIONAL" id="q-hosting">How often do you host guests?</FieldLabel>
            <ToggleGroup labelledBy="q-hosting" options={['Rarely', 'Occasionally', 'Frequently']} value={hosting} onChange={pick(setHosting)} />
            <Hint>Affects living/dining sizing and how open the kitchen should feel.</Hint>
          </Box>

          <Box sx={FIELD_SX}>
            <FieldLabel tag="OPTIONAL" id="q-noise">Noise sensitivity at home</FieldLabel>
            <ToggleGroup
              labelledBy="q-noise"
              options={['Low — open spaces fine', 'Medium', 'High — need separation']}
              value={noise}
              onChange={pick(setNoise)}
            />
            <Hint>Informs study placement, door vs open-frame, and kitchen enclosure.</Hint>
          </Box>

          <Box sx={FIELD_SX}>
            <FieldLabel tag="OPTIONAL" id="q-morning">
              Do all occupants leave home at roughly the same time each morning?
            </FieldLabel>
            <ToggleGroup
              labelledBy="q-morning"
              options={['Yes, roughly together', 'No, staggered']}
              value={morningSync}
              onChange={pick(setMorningSync)}
            />
            <Hint>Helps assess whether a single bathroom is sufficient.</Hint>
          </Box>

          <Box sx={FIELD_SX}>
            <FieldLabel tag="OPTIONAL" id="q-maintenance">Willingness to maintain finishes and materials</FieldLabel>
            <ToggleGroup
              labelledBy="q-maintenance"
              options={['Low — easy clean only', 'Medium', 'High — happy to care for natural materials']}
              value={maintenance}
              onChange={pick(setMaintenance)}
            />
            <Hint>Shapes the material palette — marble, solid timber, and some tiles need more upkeep.</Hint>
          </Box>

          <Box sx={FIELD_SX}>
            <FieldLabel tag="OPTIONAL" id="q-future">Expected changes in household size (next 3–5 years)</FieldLabel>
            <Box role="group" aria-labelledby="q-future" sx={TILE_GRID_SX}>
              {FUTURE_TILES.map(({ key, icon }) => (
                <Tile key={key} label={key} icon={icon}
                  selected={futureChanges[key]} onToggle={() => toggle(setFutureChanges, key)} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* ── 04 YOUR DREAM HOME ── */}
        <Box component="section" sx={{ mb: 6 }}>
          <ClusterHeader label="YOUR DREAM HOME" />

          <Typography sx={{
            fontSize: '13px', color: MUTED, lineHeight: 1.7, mb: '28px',
            fontStyle: 'italic', ...bodyFont,
          }}>
            These are open-ended — write as much or as little as you'd like.
            Even a sentence or two gives your designer a clearer picture than any checklist can.
          </Typography>

          <OpenQuestion
            id="q-dream-feeling"
            label="How do you want to feel when you walk through your front door?"
            value={dreamFeeling}
            onChange={type(setDreamFeeling)}
            onBlur={typeDone}
            placeholder="e.g. Calm and unhurried, like checking into a quiet hotel. Nothing on the counters. Warm light."
          />
          <OpenQuestion
            id="q-dream-inspiration"
            label="Describe a space you've been in — anywhere — that felt exactly right to you."
            value={dreamInspiration}
            onChange={type(setDreamInspiration)}
            onBlur={typeDone}
            placeholder="e.g. A friend's apartment in Tokyo — low furniture, lots of natural light, everything had a place."
          />
          <OpenQuestion
            id="q-dream-fix"
            label="What does your current home get wrong that you most want to fix?"
            value={dreamFix}
            onChange={type(setDreamFix)}
            onBlur={typeDone}
            placeholder="e.g. The kitchen feels cut off from everything. There's never enough storage. It's dark even during the day."
          />
          <OpenQuestion
            id="q-dream-dislikes"
            label="Any materials, colours, or styles you'd never want in your home?"
            value={dreamDislikes}
            onChange={type(setDreamDislikes)}
            onBlur={typeDone}
            placeholder="e.g. No high-gloss finishes. Not a fan of grey. Anything that feels cold or clinical."
          />
        </Box>

        {/* ── 05 BUDGET & NOTES ── */}
        <Box component="section" sx={{ mb: 2 }}>
          <ClusterHeader label="BUDGET & NOTES" />

          <Box sx={FIELD_SX}>
            <FieldLabel tag="OPTIONAL" id="q-budget">Rough renovation budget</FieldLabel>
            <ToggleGroup labelledBy="q-budget" options={['S$25k–50k', 'S$50k–75k', 'S$75k–100k', '>S$100k']} value={budget} onChange={pick(setBudget)} />
            <Hint>A rough sense of budget helps designers calibrate their material and finish recommendations.</Hint>
          </Box>

          <OpenQuestion
            id="q-notes"
            label="Anything else your designer should know?"
            value={notes}
            onChange={type(setNotes)}
            onBlur={typeDone}
            placeholder="e.g. We work from home three days a week. Our son plays the piano. We'd like to keep the existing kitchen tiles."
          />
        </Box>

      </Box>
    </Box>
  );
}
